import {alpha, SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

export const tableContainerSx: SxProps<Theme> = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
};

export const tableSx: SxProps<Theme> = {
    border: 'none',
    borderRadius: 0,
    '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
        outline: 'none !important',
    },
    // Vertically center cell content (chips / actions) against the
    // multi-line multilingual description.
    '& .MuiDataGrid-cell': {
        alignItems: 'center',
    },
};

export const qtyCellSx: SxProps<Theme> = {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontFeatureSettings: '"tnum"',
};

export const priceCellSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0.25,
    py: 0.5,
};

export const vatCaptionSx: SxProps<Theme> = {
    fontSize: '0.65rem',
    color: theme.palette.neutral[500],
    letterSpacing: '0.02em',
};

export const mutedDashSx: SxProps<Theme> = {
    color: theme.palette.neutral[400],
};

export const invoiceLinkSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
        textDecoration: 'underline',
    },
};

// --- Multilingual description ---------------------------------------------
export const descriptionCellSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
    py: 1,
    width: '100%',
};

export const langLineSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0,
};

export const langPillSx = (primary: boolean): SxProps<Theme> => ({
    flexShrink: 0,
    minWidth: 28,
    textAlign: 'center',
    fontSize: '0.625rem',
    fontWeight: 700,
    lineHeight: '18px',
    letterSpacing: '0.04em',
    borderRadius: theme.spacing(0.5),
    color: primary ? theme.palette.primary.main : theme.palette.neutral[500],
    backgroundColor: primary
        ? alpha(theme.palette.primary.main, 0.12)
        : theme.palette.neutral[100],
});

export const langTextSx = (primary: boolean): SxProps<Theme> => ({
    fontSize: primary ? '0.875rem' : '0.8125rem',
    fontWeight: primary ? 600 : 400,
    color: primary ? theme.palette.text.primary : theme.palette.neutral[600],
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

// --- Price chip (restored) -------------------------------------------------
export const priceChipSx: SxProps<Theme> = {
    fontWeight: 600,
    color: theme.palette.success.dark,
    backgroundColor: alpha(theme.palette.success.main, 0.12),
    border: 'none',
};

// --- Row actions (matches the sibling tables' RfidActions style) -----------
export const actionsRootSx: SxProps<Theme> = {
    flexDirection: 'row',
    justifyContent: 'flex-end',
};

export const actionsButtonSx: SxProps<Theme> = {
    width: '28px',
    height: '28px',
    p: '2px',
    color: theme.palette.neutral[400],
    backgroundColor: 'transparent',
    '.MuiSvgIcon-root': {
        fill: theme.palette.neutral[400],
    },
    '&:not(:last-child)': {
        mr: 1,
    },
};

export const actionsIconSx: SxProps<Theme> = {
    width: '20px',
};

// --- Footer "add" button ---------------------------------------------------
export const addButtonSx: SxProps<Theme> = {
        // The whole footer, not a button sitting in it: the row is the affordance, so it spans the
        // card edge to edge under the last table row.
        width: '100%',
    padding: theme.spacing(10 / 8, 16 / 8),
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:active': {
        backgroundColor: theme.palette.action.selected,
    },
};
